import { authTokenCache } from '@/lib/auth-token';
import type { EmailCheckResponse } from '@/cockpit/email-check';

// One POST, so no shared request helper. Every outcome is data, never thrown.

const PATH = '/bff/emails/verify';

export type EmailCheckSurface = 'cockpit' | 'manual';

async function authHeader(): Promise<Record<string, string>> {
  const token = await authTokenCache.get();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export async function checkEmail(
  email: string,
  surface: EmailCheckSurface,
  signal: AbortSignal,
  // A rejected token is worth one fresh mint, never two.
  retry = true,
): Promise<EmailCheckResponse> {
  const headers = await authHeader();
  let res: Response;
  try {
    res = await fetch(PATH, {
      method: 'POST',
      headers: { ...headers, 'content-type': 'application/json' },
      body: JSON.stringify({ email, surface }),
      signal,
    });
  } catch {
    // An abort lands here too; the caller discards the answer either way.
    return { ok: false, reason: 'upstream' };
  }

  // Most likely the cached token expired — but only if one was actually sent.
  if (res.status === 401 && retry && headers.authorization) {
    authTokenCache.invalidate();
    return checkEmail(email, surface, signal, false);
  }

  // A route this bundle knows and the server does not: they deploy independently.
  if (res.status === 404) {
    return { ok: false, reason: 'unconfigured' };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, reason: 'upstream' };
  }

  return parseResponse(body);
}

// The edge owns the verdict shape and may grow one; anything else is a failure.
function parseResponse(body: unknown): EmailCheckResponse {
  if (typeof body !== 'object' || body === null) return { ok: false, reason: 'upstream' };
  const frame = body as Record<string, unknown>;

  if (frame.ok === true && typeof frame.verdict === 'object' && frame.verdict !== null) {
    const verdict = frame.verdict as Record<string, unknown>;
    const kind = verdict.kind;
    if (kind === 'valid') return { ok: true, verdict: { kind, free: verdict.free === true } };
    if (kind === 'unconfirmed') {
      return {
        ok: true,
        verdict: {
          kind,
          free: verdict.free === true,
          risky: verdict.risky === true,
          catchAll: verdict.catchAll === true,
        },
      };
    }
    if (kind === 'invalid' || kind === 'unchecked') return { ok: true, verdict: { kind } };
    return { ok: false, reason: 'upstream' };
  }

  return {
    ok: false,
    reason: typeof frame.reason === 'string' ? frame.reason : 'upstream',
    ref: typeof frame.ref === 'string' ? frame.ref : null,
    message: typeof frame.message === 'string' ? frame.message : null,
  };
}
